import React, { useContext } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';

import metadata from './MLClaimDetailPopup.metadata.json5';
import styles from './MLClaimDetailPopup.module.scss';
import messages from './MLClaimDetailPopup.messages';

import mlKeyContactsComponent from '../MLKeyContactsComponent/MLKeyContactsComponent';
import mlPeopleInvolvedComponent from '../MLPeopleInvlolvedComponent/MLPeopleInvolvedComponent';
import mlClaimPaymentComponent from '../MLClaimPaymentsComponent/MLClaimPaymentsComponent';
import mlClaimReserveComponent from '../MLClaimReservesComponent/MLClaimReservesComponent';
import mlVehiclesInvolvedComponent from '../MLVehiclesInvolvedComponent/MLVehiclesInvolvedComponent';
import mlPolicyCoveragesComponent from '../MLPolicyCoveragesComponent/MLPolicyCoveragesComponent';

import { IntlContext } from '@jutro/legacy/locale';

const MLClaimDetailPopup = (props) => {
    const {
        adjuster,
        claimContacts,
        claimDetail,
        payments,
        policyCoverages,
        reserves,
        policyType,
        policyVehicles,
        policy,
        claimVehicles,
        vehicleIncidents,
        onCancel,
    } = props;
    
    const translator = useTranslator();
    const intl = useContext(IntlContext);

    const HOMEOWNERS_POLICY_TYPE = 'HOPHomeowners';
    const PERSONAL_AUTO_POLICY_TYPE = 'PersonalAuto';
    const INSURED = 'insured';
    const CLAIMANT = 'claimant';
    
    const addressLine1 = !_.isUndefined(claimDetail.lossLocation) ? `${claimDetail.lossLocation.addressLine1}` : ``;
    const addressLine2 = !_.isUndefined(claimDetail.lossLocation) ? `${claimDetail.lossLocation.city}, ${claimDetail.lossLocation.state} ${claimDetail.lossLocation.postalCode}` : ``;
    
    let mainDriverIncident;
    let otherDriversIncidents;
    let claimant;
    if (policyType !== HOMEOWNERS_POLICY_TYPE) {
        mainDriverIncident = vehicleIncidents.find((incident) => incident.vehicleLossParty_ML === INSURED);
        otherDriversIncidents = vehicleIncidents.filter((incident) => incident.vehicleLossParty_ML !== INSURED);
    }

    if (policyType !== PERSONAL_AUTO_POLICY_TYPE) {
        claimant = claimContacts.find((contact) => contact.contactRoles.includes(CLAIMANT));
    }

    const showSection = () => {
        return (!_.isUndefined(mainDriverIncident) && !_.isUndefined(mainDriverIncident.driver)) ||
            (otherDriversIncidents && otherDriversIncidents.find((incident) => incident.driver)) ||
            !_.isUndefined(claimant);
    };

    const formatPhoneNumber = (number) => {
        if (_.isNil(number) || number.length !== 10) {
            return number;
        }

        const area = number.substring(0, 3);
        const front = number.substring(3, 6);
        const end = number.substring(6, 10);

        return `${area} ${front}-${end}`;
    }

    const formatDateForClaims = (time) => {
        if(time === undefined){
            return '-';
        }
        return intl.formatDate(time, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric'}).replace(',', '');
    }

    const overrideProps = {
        keycontactDetailComponent: {
            adjuster: adjuster,
            claimContacts: claimContacts,
            policyType: policyType,
            formatPhoneNumber: formatPhoneNumber
        },
        summaryDetail: {
            content: (
                <div>
                    <hr />
                    <h2>{translator(messages.detailedSummary)}</h2>
                    <div className="row">
                        <div className="col-md-4">
                            <div className="vertical-spacing-20"><b>{translator(messages.claimNumber, { number: claimDetail.claimNumber })}</b></div>
                            <div>{translator(messages.claimStatus, { status: claimDetail.claimState })}</div>
                            <div>{translator(messages.dateOfLoss)}</div>
                            <div>{formatDateForClaims(claimDetail.lossDate_ext)}</div>
                            <div>{translator(messages.dateOfLossSubmitted)}</div>
                            <div>{formatDateForClaims(claimDetail.dateReported_ML)}</div>
                        </div>
                        <div className="col-md-4">
                            <div className="vertical-spacing-20"><b>{translator(messages.location)}</b></div>
                            <div>{addressLine1}</div>
                            <div>{addressLine2}</div>
                        </div>
                        <div className="col-md-4">
                            <div className="vertical-spacing-20"><b>{translator(messages.detailedDescription)}</b></div>
                            <div>{claimDetail.description}</div>
                        </div>
                    </div>
                </div>
            )
        },
        paymentDetailContainer: {
            visible: payments.length > 0
        },
        reserveDetailContainer: {
            visible: reserves.length > 0
        },
        paymentDetailComponent: {
            payments: payments,
            totalAmountPaid: claimDetail.totalAmountPaid_Ext

        },
        reserveDetailComponent: {
            reserves: reserves
        },
        peopleInvolvedContainer: {
            visible: showSection()
        },
        peopleInvolvedComponent: {
            mainDriverIncident : mainDriverIncident,
            otherDriversIncidents:otherDriversIncidents,
            claimant : claimant,
            formatPhoneNumber: formatPhoneNumber

        },
        vehicleInvolvedContainer: {
            visible: policyType !== HOMEOWNERS_POLICY_TYPE && claimVehicles.length > 0
        },
        vehiclesInvolvedComponent: {
            vehicles: claimVehicles
        },
        policyCoveragesComponent: {
            policyCoverages: policyCoverages,
            policyVehicles: policyVehicles,
            policy: policy
        }

    };

    const resolvers = {
        resolveComponentMap: {
            mlKeyContactDetailComponent: mlKeyContactsComponent,
            mlPeopleInvolvedComponent: mlPeopleInvolvedComponent,
            mlClaimPaymentComponent: mlClaimPaymentComponent,
            mlClaimReserveComponent: mlClaimReserveComponent,
            mlVehiclesInvolvedComponent: mlVehiclesInvolvedComponent,
            mlPolicyCoveragesComponent: mlPolicyCoveragesComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap : {
            onClose: onCancel 
        }
    };

    return  (
        <ViewModelForm 
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    )
};

MLClaimDetailPopup.propTypes = {
    adjuster: PropTypes.object.isRequired,
    claimContacts: PropTypes.arrayOf(PropTypes.object).isRequired,
    claimDetail: PropTypes.object.isRequired,
    payments: PropTypes.arrayOf(PropTypes.object).isRequired,
    policyCoverages: PropTypes.arrayOf(PropTypes.object).isRequired,
    reserves: PropTypes.arrayOf(PropTypes.object).isRequired,
    policyType: PropTypes.string.isRequired,
    policyVehicles: PropTypes.arrayOf(PropTypes.object).isRequired,
    policy: PropTypes.object.isRequired,
    claimVehicles: PropTypes.arrayOf(PropTypes.object).isRequired,
    vehicleIncidents: PropTypes.arrayOf(PropTypes.object).isRequired,
    onCancel: PropTypes.func,
};

export default MLClaimDetailPopup;
